// Service worker registration (D-007).
//
// The app previously shipped no registration call at all, which is why
// installability never worked: without a registered service worker Chrome
// never fires `beforeinstallprompt`, so there is no native "add to home
// screen" flow to offer.
//
// This registers /sw.js — a network-passthrough worker — NOT the older
// /service-worker.js, which is a self-destructing worker kept in place to
// neutralize a stuck Workbox registration from a previous deploy. Registering
// that one would unregister itself and reload the page on every load.

const SW_URL = '/sw.js';

/**
 * Register the service worker. Safe to call more than once: the browser
 * de-duplicates by script URL and scope, so a repeat call resolves with the
 * existing registration rather than creating a second one.
 *
 * Failures are logged and swallowed — a browser without service worker support
 * (or a blocked registration) must never break the app; it only means the
 * native install prompt is unavailable.
 */
export function register(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  return navigator.serviceWorker
    .register(SW_URL)
    .then((registration) => registration)
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('[pwa] service worker registration failed:', error);
      return null;
    });
}

/** Escape hatch: drop the registration (used when debugging cache issues). */
export function unregister(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(false);
  }
  return navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch(() => false);
}
